import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area } from 'recharts';
import { TrendingUp, BarChart3, PieChart as PieChartIcon, Activity, Target, BookOpen } from 'lucide-react';
import api from '../../services/api';

const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4'];

export default function LecturerReports() {
  const [dashboard, setDashboard] = useState({ assigned_subjects: 0, total_exams: 0, total_attendance_sessions: 0, subjects: [] });
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSubjectIndex, setCurrentSubjectIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => { 
    loadData();
    loadStudentPerformance();
  }, []);

  // Number of assigned subjects (primitive used for stable effect dependency)
  const subjectLen = dashboard.subjects ? dashboard.subjects.length : 0;

  // Auto advance subjects carousel when more than 3 subjects
  useEffect(() => {
    const len = subjectLen;
    if (len <= 3) {
      setCurrentSubjectIndex(0);
      return;
    }
    setCurrentSubjectIndex(0);
    const timer = setInterval(() => {
      if (!isPaused) setCurrentSubjectIndex(s => (s + 1) % len);
    }, 3500);
    return () => clearInterval(timer);
  }, [subjectLen, isPaused]);

  const loadData = async () => {
    try {
      const res = await api.get('/lecturer/dashboard');
      if (res.data.success && res.data.data && res.data.data.dashboard) {
        setDashboard(res.data.data.dashboard);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const loadStudentPerformance = async () => {
    try {
      const res = await api.get('/lecturer/student-performance');
      if (res.data.success && res.data.data) {
        setPerformance(res.data.data.performance || []);
      }
    } catch (err) {
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  // Summary data
  const summaryData = [
    { name: 'Subjects', value: dashboard.assigned_subjects, fill: '#6366f1' },
    { name: 'Exams', value: dashboard.total_exams, fill: '#8b5cf6' },
    { name: 'Sessions', value: dashboard.total_attendance_sessions, fill: '#ec4899' },
  ];

  // Generate chart data from performance
  const gradeDistribution = performance.map((grade, index) => ({
    name: grade.grade_name,
    value: grade.all_students.length,
    fill: COLORS[index % COLORS.length]
  }));

  const subjectPerformance = dashboard.subjects.map((subject, index) => ({
    name: subject.subject_name,
    fullName: subject.subject_name + ' (' + subject.grade_name + ')',
    avgMarks: Math.round(subject.average_marks || 0),
    attendance: Math.round(subject.attendance_percentage || 0),
    students: subject.student_count || 0,
    fill: COLORS[index % COLORS.length]
  }));

  // Group exams by subject for exam stats
  const examStatsBySubject = {};
  dashboard.subjects.forEach(subject => {
    examStatsBySubject[subject.subject_name] = {
      subject: subject.subject_name,
      exams: subject.total_exams || 0,
      avgScore: Math.round(subject.average_marks || 0)
    };
  });
  const examStats = Object.values(examStatsBySubject);

  // Use real attendance by grade data from backend
  const attendanceTrend = dashboard.attendance_by_grade || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Reports & Analysis</h1>
        <p className="text-gray-500">Comprehensive teaching performance and student analytics</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-6">
        {summaryData.map((item, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center hover:shadow-md transition">
            <p className="text-4xl font-bold" style={{ color: item.fill }}>{item.value}</p>
            <p className="text-gray-500 mt-2 font-medium">{item.name}</p>
            <div className="mt-3 h-1 bg-gray-100 rounded-full overflow-hidden">
              {(() => {
                const vals = summaryData.map(d => d.value || 0);
                const maxVal = Math.max(...vals, 1);
                const pct = Math.round((item.value / maxVal) * 100);
                return <div style={{ backgroundColor: item.fill, width: `${pct}%`, height: '100%' }}></div>;
              })()}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Summary Bar Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-800">Teaching Summary</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={summaryData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                cursor={{ fill: 'rgba(99, 102, 241, 0.1)' }}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {summaryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Grade Distribution Pie */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <PieChartIcon className="w-5 h-5 text-pink-600" />
            <h3 className="text-lg font-semibold text-gray-800">Grade Distribution</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={gradeDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {gradeDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                formatter={(value) => `${value} students`}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Attendance Distribution by Grade */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-pink-600" />
          <h3 className="text-lg font-semibold text-gray-800">Attendance Sessions by Grade</h3>
        </div>
        {attendanceTrend.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={attendanceTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="grade_name" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" label={{ value: 'Sessions', angle: -90, position: 'insideLeft' }} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                formatter={(value) => `${value} sessions`}
              />
              <Bar 
                dataKey="sessions" 
                fill="#ec4899" 
                name="Attendance Sessions"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-gray-400 text-center py-12">No attendance data available</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Subject Performance */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-orange-600" />
            <h3 className="text-lg font-semibold text-gray-800">Subject Performance</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={subjectPerformance} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" stroke="#9ca3af" />
              <YAxis dataKey="name" type="category" stroke="#9ca3af" width={80} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                formatter={(value) => `${value}`}
              />
              <Legend />
              <Bar dataKey="avgMarks" fill="#6366f1" name="Avg Marks" radius={[0, 4, 4, 0]} />
              <Bar dataKey="attendance" fill="#ec4899" name="Attendance %" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Exam Analysis */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-800">Exam Analysis</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={examStats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="subject" stroke="#9ca3af" angle={-45} textAnchor="end" height={80} />
              <YAxis stroke="#9ca3af" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                cursor={{ fill: 'rgba(139, 92, 246, 0.1)' }}
              />
              <Legend />
              <Bar dataKey="exams" fill="#8b5cf6" name="Total Exams" radius={[4, 4, 0, 0]} />
              <Bar dataKey="avgScore" fill="#06b6d4" name="Avg Score" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Assigned Subjects (carousel) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Assigned Subjects</h3>
        {dashboard.subjects.length > 0 ? (
          <div className="relative" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
            <div className="space-y-3" style={{ minHeight: '220px' }}>
              {(() => {
                const len = dashboard.subjects.length;
                const visible = len <= 3 ? dashboard.subjects : [0,1,2].map(i => dashboard.subjects[(currentSubjectIndex + i) % len]);
                return visible.map((subject, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-indigo-50 transition">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: `${COLORS[(currentSubjectIndex + idx) % COLORS.length]}20` }}>
                        <BookOpen className="w-5 h-5" style={{ color: COLORS[(currentSubjectIndex + idx) % COLORS.length] }} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{subject.subject_name}</p>
                        <p className="text-sm text-gray-500">{subject.grade_name}</p>
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>

            {dashboard.subjects.length > 3 && (
              <div className="absolute right-2 top-2 flex gap-2">
                <button onClick={() => setCurrentSubjectIndex((s) => (s - 1 + dashboard.subjects.length) % dashboard.subjects.length)} className="p-2 bg-white rounded-full shadow-sm text-gray-600 hover:bg-gray-50">◀</button>
                <button onClick={() => setCurrentSubjectIndex((s) => (s + 1) % dashboard.subjects.length)} className="p-2 bg-white rounded-full shadow-sm text-gray-600 hover:bg-gray-50">▶</button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No subjects assigned yet</p>
        )}
      </div>

      {/* Top Performers by Grade */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-800">Top Performing Students by Grade</h3>
        </div>
        {performance.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {performance.map((gradeData, idx) => (
              <div key={idx} className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-100">
                <p className="text-sm font-semibold text-green-800 mb-3">{gradeData.grade_name}</p>
                <div className="space-y-3">
                  {(() => {
                    const filteredTop = (gradeData.top_performers || []).filter(s => (s.avg || 0) > 0);
                    return filteredTop.length > 0 ? (
                      filteredTop.map((student, sidx) => (
                        <div key={sidx} className="flex items-center justify-between p-2 bg-white rounded">
                          <div>
                            <p className="text-sm font-medium text-gray-800">{student.name}</p>
                            <p className="text-xs text-green-600">{student.exam_count} exam{student.exam_count > 1 ? 's' : ''}</p>
                          </div>
                          <span className="px-3 py-1 bg-green-200 text-green-800 text-xs font-bold rounded-full">{student.avg}%</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-gray-500 text-center py-2">No top performers yet</p>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No student data available</p>
        )}
      </div>

      {/* Students Needing Improvement by Grade */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-orange-600" />
          <h3 className="text-lg font-semibold text-gray-800">Students Needing Improvement by Grade</h3>
        </div>
        {performance.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {performance.map((gradeData, idx) => (
              <div key={idx} className="p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-lg border border-orange-100">
                <p className="text-sm font-semibold text-orange-800 mb-3">{gradeData.grade_name}</p>
                <div className="space-y-3">
                  {(() => {
                    const filteredNeeds = (gradeData.needs_improvement || []).filter(s => (s.avg || 0) > 0);
                    return filteredNeeds.length > 0 ? (
                      filteredNeeds.map((student, sidx) => (
                        <div key={sidx} className="flex items-center justify-between p-2 bg-white rounded">
                          <div>
                            <p className="text-sm font-medium text-gray-800">{student.name}</p>
                            <p className="text-xs text-orange-600">{student.exam_count} exam{student.exam_count > 1 ? 's' : ''}</p>
                          </div>
                          <span className="px-3 py-1 bg-orange-200 text-orange-800 text-xs font-bold rounded-full">{student.avg}%</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-gray-500 text-center py-2">All students performing well!</p>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No student data available</p>
        )}
      </div>
   
    </div>
  );
  
}